"use client";

import {
  type Announcements,
  type CollisionDetection,
  closestCenter,
  closestCorners,
  DndContext,
  type DndContextProps,
  type DragCancelEvent,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  type DroppableContainer,
  defaultDropAnimationSideEffects,
  getFirstCollision,
  KeyboardCode,
  type KeyboardCoordinateGetter,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  type UniqueIdentifier,
  useDndContext,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  type AnimateLayoutChanges,
  arrayMove,
  defaultAnimateLayoutChanges,
  horizontalListSortingStrategy,
  SortableContext,
  type SortableContextProps,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import * as ReactDOM from "react-dom";

import { useComposedRefs } from "@/modern/compose-refs";
import { cn } from "@/modern/utils";

const directions: string[] = [
  KeyboardCode.Down,
  KeyboardCode.Right,
  KeyboardCode.Up,
  KeyboardCode.Left,
];

const coordinateGetter: KeyboardCoordinateGetter = (event, { context }) => {
  const { active, droppableRects, droppableContainers, collisionRect } =
    context;

  if (directions.includes(event.code)) {
    event.preventDefault();

    if (!active || !collisionRect) return;

    const filteredContainers: DroppableContainer[] = [];

    for (const entry of droppableContainers.getEnabled()) {
      if (!entry || entry?.disabled) return;

      const rect = droppableRects.get(entry.id);

      if (!rect) return;

      const data = entry.data.current;

      if (data) {
        const { type, children } = data;

        if (type === "container" && children?.length > 0) {
          if (active.data.current?.type !== "container") {
            return;
          }
        }
      }

      switch (event.code) {
        case KeyboardCode.Down:
          if (collisionRect.top < rect.top) {
            filteredContainers.push(entry);
          }
          break;
        case KeyboardCode.Up:
          if (collisionRect.top > rect.top) {
            filteredContainers.push(entry);
          }
          break;
        case KeyboardCode.Left:
          if (collisionRect.left >= rect.left + rect.width) {
            filteredContainers.push(entry);
          }
          break;
        case KeyboardCode.Right:
          if (collisionRect.left + collisionRect.width <= rect.left) {
            filteredContainers.push(entry);
          }
          break;
      }
    }

    const collisions = closestCorners({
      active,
      collisionRect: collisionRect,
      droppableRects,
      droppableContainers: filteredContainers,
      pointerCoordinates: null,
    });
    const closestId = getFirstCollision(collisions, "id");

    if (closestId != null) {
      const newDroppable = droppableContainers.get(closestId);
      const newNode = newDroppable?.node.current;
      const newRect = newDroppable?.rect.current;

      if (newNode && newRect) {
        if (newDroppable.id === "placeholder") {
          return {
            x: newRect.left + (newRect.width - collisionRect.width) / 2,
            y: newRect.top + (newRect.height - collisionRect.height) / 2,
          };
        }

        if (newDroppable.data.current?.type === "container") {
          return {
            x: newRect.left + 20,
            y: newRect.top + 74,
          };
        }

        return {
          x: newRect.left,
          y: newRect.top,
        };
      }
    }
  }

  return undefined;
};

const ROOT_NAME = "Kanban";
const BOARD_NAME = "KanbanBoard";
const COLUMN_NAME = "KanbanColumn";
const COLUMN_HANDLE_NAME = "KanbanColumnHandle";
const ITEM_NAME = "KanbanItem";
const ITEM_HANDLE_NAME = "KanbanItemHandle";
const OVERLAY_NAME = "KanbanOverlay";

/** Separator for composite item ids (columnId + itemId) so the same record can appear in multiple columns with unique draggable ids */
const COMPOSITE_ID_SEPARATOR = "__";

function parseCompositeId(id: UniqueIdentifier): { columnId: string; baseId: string } | null {
  if (id == null || typeof id !== "string") return null;
  const idx = String(id).indexOf(COMPOSITE_ID_SEPARATOR);
  if (idx <= 0) return null;
  return {
    columnId: String(id).slice(0, idx),
    baseId: String(id).slice(idx + COMPOSITE_ID_SEPARATOR.length),
  };
}

function getItemBaseId(id: UniqueIdentifier): UniqueIdentifier {
  const parsed = parseCompositeId(id);
  return parsed ? parsed.baseId : id;
}

interface KanbanColumn<T> {
  id: UniqueIdentifier;
  value: string | number | any[] | null;
  title: string;
  data: T[];
}

interface KanbanContextValue<T> {
  id: string;
  columns: KanbanColumn<T>[];
  modifiers: DndContextProps["modifiers"];
  strategy: SortableContextProps["strategy"];
  orientation: "horizontal" | "vertical";
  activeId: UniqueIdentifier | null;
  setActiveId: (id: UniqueIdentifier | null) => void;
  getItemValue: (item: T, columnId?: UniqueIdentifier) => UniqueIdentifier;
  getItemBaseId: (id: UniqueIdentifier) => UniqueIdentifier;
  /** True when dragging over a column that already contains this item (invalid drop) */
  isOverInvalidDropTarget: boolean;
  flatCursor: boolean;
  disableItemSorting: boolean;
}

const KanbanContext = React.createContext<KanbanContextValue<unknown> | null>(
  null,
);

function useKanbanContext(consumerName: string) {
  const context = React.useContext(KanbanContext);
  if (!context) {
    throw new Error(`\`${consumerName}\` must be used within \`${ROOT_NAME}\``);
  }
  return context;
}

interface GetItemValue<T> {
  /**
   * Callback that returns a unique identifier for each kanban item. Required for array of objects.
   * When the same item can appear in multiple columns (e.g. multi-value tags), pass columnId as second
   * argument and return a composite id so each placement has a unique draggable id.
   * @example getItemValue={(item) => item.id}
   * @example getItemValue={(item, columnId) => columnId != null ? `${columnId}__${item.id}` : item.id}
   */
  getItemValue: (item: T, columnId?: UniqueIdentifier) => UniqueIdentifier;
}

export interface KanbanColumnsPersistPayload<T> {
  newColumns: KanbanColumn<T>[];
  movedItem?: T;
  targetColumnValue?: KanbanColumn<T>["value"];
}

type KanbanProps<T> = Omit<DndContextProps, "collisionDetection"> &
  (T extends object ? GetItemValue<T> : Partial<GetItemValue<T>>) & {
    columns: KanbanColumn<T>[];
    onColumnsChange?: (columns: KanbanColumn<T>[]) => void;
    onColumnsPersist?: (payload: KanbanColumnsPersistPayload<T>) => void;
    onMove?: (
      event: DragEndEvent & { activeIndex: number; overIndex: number; newColumns: KanbanColumn<T>[] },
    ) => void;
    strategy?: SortableContextProps["strategy"];
    orientation?: "horizontal" | "vertical";
    flatCursor?: boolean;
    disableItemSorting?: boolean;
  };

function Kanban<T>(props: KanbanProps<T>) {
  const {
    columns: columnsProp,
    onColumnsChange,
    onColumnsPersist,
    modifiers,
    strategy = verticalListSortingStrategy,
    orientation = "horizontal",
    onMove,
    getItemValue: getItemValueProp,
    accessibility,
    flatCursor = false,
    disableItemSorting = false,
    ...kanbanProps
  } = props;

  const id = React.useId();
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [isOverInvalidDropTarget, setIsOverInvalidDropTarget] = React.useState(false);
  const [columns, setColumns] = React.useState<KanbanColumn<T>[]>(columnsProp);
  const columnsRef = React.useRef<KanbanColumn<T>[]>(columnsProp);
  const lastOverIdRef = React.useRef<UniqueIdentifier | null>(null);
  const hasMovedRef = React.useRef(false);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
  );

  // Sync internal state with prop changes
  React.useEffect(() => {
    setColumns(columnsProp);
    columnsRef.current = columnsProp;
  }, [columnsProp]);

  // Keep ref in sync with state
  React.useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const getItemValue = React.useCallback(
    (item: T, columnId?: UniqueIdentifier): UniqueIdentifier => {
      if (typeof item === "object" && !getItemValueProp) {
        throw new Error(
          "`getItemValue` is required when using array of objects",
        );
      }
      const baseId = getItemValueProp
        ? getItemValueProp(item)
        : (item as UniqueIdentifier);
      if (columnId != null && columnId !== "") {
        return `${String(columnId)}${COMPOSITE_ID_SEPARATOR}${baseId}`;
      }
      return baseId;
    },
    [getItemValueProp],
  );

  const getColumn = React.useCallback(
    (id: UniqueIdentifier) => {
      const currentColumns = columnsRef.current;
      const currentColumnIds = currentColumns.map(col => col.id);

      // Check if id is a column id
      if (currentColumnIds.includes(id)) return id;

      const parsed = parseCompositeId(id);
      if (parsed) return parsed.columnId;

      // Check if id is an item in any column (non-composite id)
      for (const column of currentColumns) {
        if (column.data.some((item) => getItemValue(item, column.id) === id)) {
          return column.id;
        }
      }

      return null;
    },
    [getItemValue],
  );

  const isDropTargetInvalid = React.useCallback(
    (activeIdArg: UniqueIdentifier, overColumnIdArg: UniqueIdentifier) => {
      const currentColumns = columnsRef.current;
      const overColumn = currentColumns.find(col => col.id === overColumnIdArg);
      if (!overColumn) return false;
      const activeBaseId = getItemBaseId(activeIdArg);
      return overColumn.data.some((item) => {
        const itemId = getItemValue(item);
        return itemId == activeBaseId || String(itemId) === String(activeBaseId);
      });
    },
    [getItemValue],
  );

  const collisionDetection: CollisionDetection = React.useCallback(
    (args) => {
      const currentColumns = columnsRef.current;
      const currentColumnIds = currentColumns.map(col => col.id);

      if (activeId && currentColumnIds.includes(activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) => currentColumnIds.includes(container.id),
          ),
        });
      }

      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, "id");

      if (!overId) {
        if (hasMovedRef.current) {
          lastOverIdRef.current = activeId;
        }
        return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : [];
      }

      const overColumn = currentColumns.find(col => col.id === overId);
      if (overColumn) {
        const containerItems = overColumn.data;
        if (containerItems && containerItems.length > 0) {
          const closestItem = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter(
              (container) =>
                container.id !== overId &&
                containerItems.some(
                  (item) => getItemValue(item, overColumn.id) === container.id,
                ),
            ),
          });

          if (closestItem.length > 0) {
            overId = closestItem[0]?.id ?? overId;
          }
        }
      }

      lastOverIdRef.current = overId;
      return [{ id: overId }];
    },
    [activeId, getItemValue],
  );

  const onDragStart = React.useCallback(
    (event: DragStartEvent) => {
      kanbanProps.onDragStart?.(event);

      if (event.activatorEvent.defaultPrevented) return;
      setActiveId(event.active.id);
      setIsOverInvalidDropTarget(false);
    },
    [kanbanProps.onDragStart],
  );

  const onDragOver = React.useCallback(
    (event: DragOverEvent) => {
      kanbanProps.onDragOver?.(event);

      if (event.activatorEvent.defaultPrevented) return;

      const { active, over } = event;
      if (!over) {
        setIsOverInvalidDropTarget(false);
        return;
      }

      const currentColumns = columnsRef.current;
      const activeColumnId = getColumn(active.id);
      const overColumnId = getColumn(over.id);

      if (!activeColumnId || !overColumnId) {
        setIsOverInvalidDropTarget(false);
        return;
      }

      if (activeColumnId !== overColumnId) {
        setIsOverInvalidDropTarget(isDropTargetInvalid(active.id, overColumnId));
      } else {
        setIsOverInvalidDropTarget(false);
      }

      if (activeColumnId === overColumnId) {
        // Skip reordering within same column if item sorting is disabled
        if (disableItemSorting) return;

        const column = currentColumns.find(col => col.id === activeColumnId);
        if (!column) return;

        const items = column.data;
        const activeIndex = items.findIndex(
          (item) => getItemValue(item, activeColumnId) === active.id,
        );
        const overIndex = items.findIndex(
          (item) => getItemValue(item, activeColumnId) === over.id,
        );

        if (activeIndex !== overIndex) {
          const newColumns = currentColumns.map(col => {
            if (col.id === activeColumnId) {
              return {
                ...col,
                data: arrayMove(items, activeIndex, overIndex),
              };
            }
            return col;
          });
          setColumns(newColumns);
          columnsRef.current = newColumns;
          onColumnsChange?.(newColumns);
        }
      }
      // Cross-column move: do NOT update state here. Wait for onDragEnd so the drag
      // continues until the user actually drops. Updating here would remove the item
      // from the source column and break the drag.
    },
    [disableItemSorting, getColumn, getItemValue, isDropTargetInvalid, onColumnsChange, kanbanProps.onDragOver],
  );

  const onDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      kanbanProps.onDragEnd?.(event);

      if (event.activatorEvent.defaultPrevented) return;

      const { active, over } = event;

      if (!over) {
        setActiveId(null);
        hasMovedRef.current = false;
        return;
      }

      let shouldPersist = false;
      let didCallPersist = false;
      const currentColumns = columnsRef.current;
      const currentColumnIds = currentColumns.map(col => col.id);

      // Handle column reordering
      if (currentColumnIds.includes(active.id) && currentColumnIds.includes(over.id)) {
        const activeIndex = currentColumnIds.indexOf(active.id);
        const overIndex = currentColumnIds.indexOf(over.id);

        if (activeIndex !== overIndex) {
          const newColumns = arrayMove(currentColumns, activeIndex, overIndex);
          setColumns(newColumns);
          columnsRef.current = newColumns;
          shouldPersist = true;

          if (onMove) {
            onMove({ ...event, activeIndex, overIndex, newColumns });
          } else {
            onColumnsChange?.(newColumns);
          }
        }
      } else {
        // Handle item reordering within or between columns
        const activeColumnId = getColumn(active.id);
        const overColumnId = getColumn(over.id);

        if (!activeColumnId || !overColumnId) {
          setActiveId(null);
          hasMovedRef.current = false;
          return;
        }

        if (activeColumnId === overColumnId) {
          // If item moved between columns (hasMovedRef is true), we need to persist
          if (hasMovedRef.current) {
            shouldPersist = true;
          } else if (disableItemSorting) {
            // Skip reordering within same column if item sorting is disabled
            setActiveId(null);
            hasMovedRef.current = false;
            return;
          } else {
            // Normal sorting within same column
            const column = currentColumns.find(col => col.id === activeColumnId);
            if (!column) {
              setActiveId(null);
              hasMovedRef.current = false;
              return;
            }

            const items = column.data;
            const activeIndex = items.findIndex(
              (item) => getItemValue(item, activeColumnId) === active.id,
            );
            const overIndex = items.findIndex(
              (item) => getItemValue(item, activeColumnId) === over.id,
            );

            if (activeIndex !== overIndex) {
              const newColumns = currentColumns.map(col => {
                if (col.id === activeColumnId) {
                  return {
                    ...col,
                    data: arrayMove(items, activeIndex, overIndex),
                  };
                }
                return col;
              });

              setColumns(newColumns);
              columnsRef.current = newColumns;
              shouldPersist = true;

              if (onMove) {
                onMove({
                  ...event,
                  activeIndex,
                  overIndex,
                  newColumns,
                });
              } else {
                onColumnsChange?.(newColumns);
              }
            }
          }
        } else {
          // Item moved between columns: allow only if target column doesn't already contain this item
          if (isDropTargetInvalid(active.id, overColumnId)) {
            setActiveId(null);
            setIsOverInvalidDropTarget(false);
            hasMovedRef.current = false;
            return;
          }

          const activeColumn = currentColumns.find(col => col.id === activeColumnId);
          const overColumn = currentColumns.find(col => col.id === overColumnId);

          if (activeColumn && overColumn) {
            const activeIndex = activeColumn.data.findIndex(
              (item) => getItemValue(item, activeColumnId) === active.id,
            );
            const activeItem =
              activeIndex >= 0 ? activeColumn.data[activeIndex] : undefined;

            if (activeItem) {
              const newColumns = currentColumns.map(col => {
                if (col.id === activeColumnId) {
                  return {
                    ...col,
                    data: col.data.filter(
                      (item) => getItemValue(item, activeColumnId) !== active.id,
                    ),
                  };
                }
                if (col.id === overColumnId) {
                  return {
                    ...col,
                    data: [...col.data, activeItem],
                  };
                }
                return col;
              });
              setColumns(newColumns);
              columnsRef.current = newColumns;
              onColumnsChange?.(newColumns);
              shouldPersist = true;
              // Call persist immediately with the item we have (don't rely on later lookup)
              onColumnsPersist?.({
                newColumns,
                movedItem: activeItem,
                targetColumnValue: overColumn.value,
              });
              didCallPersist = true;
            }
          }
        }
      }

      // Persist changes for other cases (column reorder, same-column reorder)
      if (shouldPersist && !onMove && !didCallPersist) {
        const newColumns = columnsRef.current;
        const currentColumnIds = newColumns.map(col => col.id);
        const wasColumnReorder =
          currentColumnIds.includes(active.id) && currentColumnIds.includes(over.id);

        let movedItem: T | undefined;
        let targetColumnValue: KanbanColumn<T>["value"] | undefined;

        if (!wasColumnReorder && over) {
          const overColumnId = getColumn(over.id);
          const targetColumn = overColumnId
            ? newColumns.find(col => col.id === overColumnId)
            : undefined;
          if (targetColumn) {
            targetColumnValue = targetColumn.value;
            const activeBaseId = getItemBaseId(active.id);
            movedItem = targetColumn.data.find(
              (it) => getItemValue(it) === activeBaseId,
            ) as T | undefined;
          }
        }

        onColumnsPersist?.({ newColumns, movedItem, targetColumnValue });
      }

      setActiveId(null);
      setIsOverInvalidDropTarget(false);
      hasMovedRef.current = false;
    },
    [
      disableItemSorting,
      getColumn,
      getItemValue,
      isDropTargetInvalid,
      onColumnsChange,
      onColumnsPersist,
      onMove,
      kanbanProps.onDragEnd,
    ],
  );

  const onDragCancel = React.useCallback(
    (event: DragCancelEvent) => {
      kanbanProps.onDragCancel?.(event);

      if (event.activatorEvent.defaultPrevented) return;

      setActiveId(null);
      setIsOverInvalidDropTarget(false);
      hasMovedRef.current = false;
    },
    [kanbanProps.onDragCancel],
  );

  const announcements: Announcements = React.useMemo(
    () => ({
      onDragStart({ active }) {
        const currentColumns = columnsRef.current;
        const currentColumnIds = currentColumns.map(col => col.id);
        const isColumn = currentColumnIds.includes(active.id);
        const itemType = isColumn ? "column" : "item";
        const position = isColumn
          ? currentColumnIds.indexOf(active.id) + 1
          : (() => {
            const columnId = getColumn(active.id);
            const column = currentColumns.find(col => col.id === columnId);
            if (!column) return 1;
            return (
              column.data.findIndex(
                (item) => getItemValue(item, column.id) === active.id,
              ) + 1
            );
          })();
        const total = isColumn
          ? currentColumns.length
          : (() => {
            const columnId = getColumn(active.id);
            const column = currentColumns.find(col => col.id === columnId);
            return column ? column.data.length : 0;
          })();

        return `Picked up ${itemType} at position ${position} of ${total}`;
      },
      onDragOver({ active, over }) {
        if (!over) return;

        const currentColumns = columnsRef.current;
        const currentColumnIds = currentColumns.map(col => col.id);
        const isColumn = currentColumnIds.includes(active.id);
        const itemType = isColumn ? "column" : "item";
        const position = isColumn
          ? currentColumnIds.indexOf(over.id) + 1
          : (() => {
            const columnId = getColumn(over.id);
            const column = currentColumns.find(col => col.id === columnId);
            if (!column) return 1;
            return (
              column.data.findIndex(
                (item) => getItemValue(item, column.id) === over.id,
              ) + 1
            );
          })();
        const total = isColumn
          ? currentColumns.length
          : (() => {
            const columnId = getColumn(over.id);
            const column = currentColumns.find(col => col.id === columnId);
            return column ? column.data.length : 0;
          })();

        const overColumn = getColumn(over.id);
        const activeColumn = getColumn(active.id);

        if (isColumn) {
          return `${itemType} is now at position ${position} of ${total}`;
        }

        if (activeColumn !== overColumn) {
          return `${itemType} is now at position ${position} of ${total} in ${overColumn}`;
        }

        return `${itemType} is now at position ${position} of ${total}`;
      },
      onDragEnd({ active, over }) {
        if (!over) return;

        const currentColumns = columnsRef.current;
        const currentColumnIds = currentColumns.map(col => col.id);
        const isColumn = currentColumnIds.includes(active.id);
        const itemType = isColumn ? "column" : "item";
        const position = isColumn
          ? currentColumnIds.indexOf(over.id) + 1
          : (() => {
            const columnId = getColumn(over.id);
            const column = currentColumns.find(col => col.id === columnId);
            if (!column) return 1;
            return (
              column.data.findIndex(
                (item) => getItemValue(item, column.id) === over.id,
              ) + 1
            );
          })();
        const total = isColumn
          ? currentColumns.length
          : (() => {
            const columnId = getColumn(over.id);
            const column = currentColumns.find(col => col.id === columnId);
            return column ? column.data.length : 0;
          })();

        const overColumn = getColumn(over.id);
        const activeColumn = getColumn(active.id);

        if (isColumn) {
          return `${itemType} was dropped at position ${position} of ${total}`;
        }

        if (activeColumn !== overColumn) {
          return `${itemType} was dropped at position ${position} of ${total} in ${overColumn}`;
        }

        return `${itemType} was dropped at position ${position} of ${total}`;
      },
      onDragCancel({ active }) {
        const currentColumns = columnsRef.current;
        const currentColumnIds = currentColumns.map(col => col.id);
        const isColumn = currentColumnIds.includes(active.id);
        const itemType = isColumn ? "column" : "item";
        return `Dragging was cancelled. ${itemType} was dropped.`;
      },
    }),
    [getColumn, getItemValue],
  );

  const contextValue = React.useMemo<KanbanContextValue<T>>(
    () => ({
      id,
      columns,
      modifiers,
      strategy,
      orientation,
      activeId,
      setActiveId,
      getItemValue,
      getItemBaseId,
      isOverInvalidDropTarget,
      flatCursor,
      disableItemSorting,
    }),
    [
      id,
      columns,
      activeId,
      isOverInvalidDropTarget,
      modifiers,
      strategy,
      orientation,
      getItemValue,
      flatCursor,
      disableItemSorting,
    ],
  );

  return (
    <KanbanContext.Provider value={contextValue as KanbanContextValue<unknown>}>
      <DndContext
        collisionDetection={collisionDetection}
        modifiers={modifiers}
        sensors={sensors}
        {...kanbanProps}
        id={id}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: `
            To pick up a kanban item or column, press space or enter.
            While dragging, use the arrow keys to move the item.
            Press space or enter again to drop the item in its new position, or press escape to cancel.
          `,
          },
          ...accessibility,
        }}
      />
    </KanbanContext.Provider>
  );
}

const KanbanBoardContext = React.createContext<boolean>(false);

interface KanbanBoardProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
  asChild?: boolean;
}

const KanbanBoard = React.forwardRef<HTMLDivElement, KanbanBoardProps>(
  function KanbanBoard(props, ref) {
    const { asChild, className, ...boardProps } = props;

    const context = useKanbanContext(BOARD_NAME);

    const columnIds = React.useMemo(() => {
      return context.columns.map(col => col.id);
    }, [context.columns]);

    const BoardPrimitive = asChild ? Slot : "div";

    return (
      <KanbanBoardContext.Provider value={true}>
        <SortableContext
          items={columnIds}
          strategy={
            context.orientation === "horizontal"
              ? horizontalListSortingStrategy
              : verticalListSortingStrategy
          }
        >
          <BoardPrimitive
            aria-orientation={context.orientation}
            data-orientation={context.orientation}
            data-slot="kanban-board"
            {...boardProps}
            ref={ref}
            className={cn(
              "flex size-full gap-4",
              context.orientation === "horizontal" ? "flex-row" : "flex-col",
              className,
            )}
          />
        </SortableContext>
      </KanbanBoardContext.Provider>
    );
  }
);

interface KanbanColumnContextValue {
  id: string;
  columnId: UniqueIdentifier;
  getItemIdForItem: (item: unknown) => UniqueIdentifier;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  setActivatorNodeRef: (node: HTMLElement | null) => void;
  isDragging?: boolean;
  disabled?: boolean;
}

const KanbanColumnContext =
  React.createContext<KanbanColumnContextValue | null>(null);

function useKanbanColumnContext(consumerName: string) {
  const context = React.useContext(KanbanColumnContext);
  if (!context) {
    throw new Error(
      `\`${consumerName}\` must be used within \`${COLUMN_NAME}\``,
    );
  }
  return context;
}

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

interface KanbanColumnProps extends React.ComponentProps<"div"> {
  value: UniqueIdentifier;
  children: React.ReactNode;
  asChild?: boolean;
  asHandle?: boolean;
  disabled?: boolean;
}

function KanbanColumn(props: KanbanColumnProps) {
  const {
    value,
    asChild,
    asHandle,
    disabled,
    className,
    style,
    ref,
    ...columnProps
  } = props;

  const id = React.useId();
  const context = useKanbanContext(COLUMN_NAME);
  const inBoard = React.useContext(KanbanBoardContext);
  const inOverlay = React.useContext(KanbanOverlayContext);
  const { over: dndOver } = useDndContext();

  if (!inBoard && !inOverlay) {
    throw new Error(
      `\`${COLUMN_NAME}\` must be used within \`${BOARD_NAME}\` or \`${OVERLAY_NAME}\``,
    );
  }

  if (value === "") {
    throw new Error(`\`${COLUMN_NAME}\` value cannot be an empty string`);
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: value,
    disabled,
    animateLayoutChanges,
  });

  const composedRef = useComposedRefs(ref, (node) => {
    if (disabled) return;
    setNodeRef(node);
  });

  const composedStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
      ...style,
    };
  }, [transform, transition, style]);

  const items = React.useMemo(() => {
    const column = context.columns.find(col => col.id === value);
    const columnData = column?.data ?? [];
    return columnData.map((item) => context.getItemValue(item, value));
  }, [context.columns, value, context.getItemValue]);

  const getItemIdForItem = React.useCallback(
    (item: unknown) => context.getItemValue(item, value),
    [context.getItemValue, value],
  );

  const isDropTarget = React.useMemo(() => {
    if (!dndOver || isDragging) return false;
    // Check if dragging over the column itself
    if (dndOver.id === value) return true;
    // Check if dragging over an item within this column
    return items.includes(dndOver.id);
  }, [dndOver, isDragging, value, items]);

  const columnContext = React.useMemo<KanbanColumnContextValue>(
    () => ({
      id,
      columnId: value,
      getItemIdForItem,
      attributes,
      listeners,
      setActivatorNodeRef,
      isDragging,
      disabled,
    }),
    [id, value, getItemIdForItem, attributes, listeners, setActivatorNodeRef, isDragging, disabled],
  );

  const ColumnPrimitive = asChild ? Slot : "div";

  return (
    <KanbanColumnContext.Provider value={columnContext}>
      <SortableContext
        items={items}
        strategy={
          context.orientation === "horizontal"
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
      >
        <ColumnPrimitive
          id={id}
          data-disabled={disabled}
          data-dragging={isDragging ? "" : undefined}
          data-slot="kanban-column"
          {...columnProps}
          {...(asHandle && !disabled ? attributes : {})}
          {...(asHandle && !disabled ? listeners : {})}
          ref={composedRef}
          style={composedStyle}
          className={cn(
            "flex size-full flex-col gap-2 rounded-lg border bg-card p-2.5 aria-disabled:pointer-events-none aria-disabled:opacity-50",
            {
              "touch-none select-none": asHandle,
              "cursor-default": context.flatCursor,
              "data-dragging:cursor-grabbing": !context.flatCursor,
              "cursor-grab": !isDragging && asHandle && !context.flatCursor,
              "opacity-50": isDragging,
              "pointer-events-none opacity-50": disabled,
              "!bg-primary/10 !border-primary !ring-2 !ring-primary/5": isDropTarget,
            },
            className,
          )}
        />
      </SortableContext>
    </KanbanColumnContext.Provider>
  );
}

interface KanbanColumnHandleProps extends React.ComponentProps<"button"> {
  asChild?: boolean;
}

function KanbanColumnHandle(props: KanbanColumnHandleProps) {
  const { asChild, disabled, className, ref, ...columnHandleProps } = props;

  const context = useKanbanContext(COLUMN_NAME);
  const columnContext = useKanbanColumnContext(COLUMN_HANDLE_NAME);

  const isDisabled = disabled ?? columnContext.disabled;

  const composedRef = useComposedRefs(ref, (node) => {
    if (isDisabled) return;
    columnContext.setActivatorNodeRef(node);
  });

  const HandlePrimitive = asChild ? Slot : "button";

  return (
    <HandlePrimitive
      type="button"
      aria-controls={columnContext.id}
      data-disabled={isDisabled}
      data-dragging={columnContext.isDragging ? "" : undefined}
      data-slot="kanban-column-handle"
      {...columnHandleProps}
      {...(isDisabled ? {} : columnContext.attributes)}
      {...(isDisabled ? {} : columnContext.listeners)}
      ref={composedRef}
      className={cn(
        "select-none disabled:pointer-events-none disabled:opacity-50",
        context.flatCursor
          ? "cursor-default"
          : "cursor-grab data-dragging:cursor-grabbing",
        className,
      )}
      disabled={isDisabled}
    />
  );
}

interface KanbanItemContextValue {
  id: string;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  setActivatorNodeRef: (node: HTMLElement | null) => void;
  isDragging?: boolean;
  disabled?: boolean;
}

const KanbanItemContext = React.createContext<KanbanItemContextValue | null>(
  null,
);

function useKanbanItemContext(consumerName: string) {
  const context = React.useContext(KanbanItemContext);
  if (!context) {
    throw new Error(`\`${consumerName}\` must be used within \`${ITEM_NAME}\``);
  }
  return context;
}

interface KanbanItemProps extends React.ComponentProps<"div"> {
  value: UniqueIdentifier;
  asHandle?: boolean;
  asChild?: boolean;
  disabled?: boolean;
}

function KanbanItem(props: KanbanItemProps) {
  const {
    value,
    style,
    asHandle,
    asChild,
    disabled,
    className,
    ref,
    ...itemProps
  } = props;

  const id = React.useId();
  const context = useKanbanContext(ITEM_NAME);
  const inBoard = React.useContext(KanbanBoardContext);
  const inOverlay = React.useContext(KanbanOverlayContext);

  if (!inBoard && !inOverlay) {
    throw new Error(`\`${ITEM_NAME}\` must be used within \`${BOARD_NAME}\``);
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value, disabled });

  if (value === "") {
    throw new Error(`\`${ITEM_NAME}\` value cannot be an empty string`);
  }

  const composedRef = useComposedRefs(ref, (node) => {
    if (disabled) return;
    setNodeRef(node);
  });

  const composedStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
      ...style,
    };
  }, [transform, transition, style]);

  const itemContext = React.useMemo<KanbanItemContextValue>(
    () => ({
      id,
      attributes,
      listeners,
      setActivatorNodeRef,
      isDragging,
      disabled,
    }),
    [id, attributes, listeners, setActivatorNodeRef, isDragging, disabled],
  );

  const ItemPrimitive = asChild ? Slot : "div";

  return (
    <KanbanItemContext.Provider value={itemContext}>
      <ItemPrimitive
        id={id}
        data-disabled={disabled}
        data-dragging={isDragging ? "" : undefined}
        data-slot="kanban-item"
        {...itemProps}
        {...(asHandle && !disabled ? attributes : {})}
        {...(asHandle && !disabled ? listeners : {})}
        ref={composedRef}
        style={composedStyle}
        className={cn(
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
          {
            "touch-none select-none": asHandle,
            "cursor-default": context.flatCursor,
            "data-dragging:cursor-grabbing": !context.flatCursor,
            "cursor-grab": !isDragging && asHandle && !context.flatCursor,
            "opacity-50": isDragging,
            "pointer-events-none opacity-50": disabled,
          },
          className,
        )}
      />
    </KanbanItemContext.Provider>
  );
}

interface KanbanItemHandleProps extends React.ComponentProps<"button"> {
  asChild?: boolean;
}

function KanbanItemHandle(props: KanbanItemHandleProps) {
  const { asChild, disabled, className, ref, ...itemHandleProps } = props;

  const context = useKanbanContext(ITEM_HANDLE_NAME);
  const itemContext = useKanbanItemContext(ITEM_HANDLE_NAME);

  const isDisabled = disabled ?? itemContext.disabled;

  const composedRef = useComposedRefs(ref, (node) => {
    if (isDisabled) return;
    itemContext.setActivatorNodeRef(node);
  });

  const HandlePrimitive = asChild ? Slot : "button";

  return (
    <HandlePrimitive
      type="button"
      aria-controls={itemContext.id}
      data-disabled={isDisabled}
      data-dragging={itemContext.isDragging ? "" : undefined}
      data-slot="kanban-item-handle"
      {...itemHandleProps}
      {...(isDisabled ? {} : itemContext.attributes)}
      {...(isDisabled ? {} : itemContext.listeners)}
      ref={composedRef}
      className={cn(
        "select-none disabled:pointer-events-none disabled:opacity-50",
        context.flatCursor
          ? "cursor-default"
          : "cursor-grab data-dragging:cursor-grabbing",
        className,
      )}
      disabled={isDisabled}
    />
  );
}

const KanbanOverlayContext = React.createContext(false);

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      // Keep drag overlay clearly visible during drag; only fade on drop
      active: {
        opacity: "1",
      },
    },
  }),
};

interface KanbanOverlayProps
  extends Omit<React.ComponentProps<typeof DragOverlay>, "children"> {
  container?: Element | DocumentFragment | null;
  children?:
  | React.ReactNode
  | ((params: {
    value: UniqueIdentifier;
    variant: "column" | "item";
    getItemBaseId: (id: UniqueIdentifier) => UniqueIdentifier;
    isOverInvalidDropTarget: boolean;
  }) => React.ReactNode);
}

function KanbanOverlay(props: KanbanOverlayProps) {
  const { container: containerProp, children, ...overlayProps } = props;

  const context = useKanbanContext(OVERLAY_NAME);

  const container =
    containerProp ??
    (typeof globalThis.document !== "undefined" ? globalThis.document.body : null);

  if (!container) return null;

  const variant =
    context.activeId && context.columns.some(col => col.id === context.activeId) ? "column" : "item";

  return ReactDOM.createPortal(
    <DragOverlay
      dropAnimation={dropAnimation}
      modifiers={context.modifiers}
      className={cn(!context.flatCursor && "cursor-grabbing")}
      {...overlayProps}
    >
      <KanbanOverlayContext.Provider value={true}>
        {context.activeId && children
          ? typeof children === "function"
            ? children({
              value: context.activeId,
              variant,
              getItemBaseId: context.getItemBaseId,
              isOverInvalidDropTarget: context.isOverInvalidDropTarget,
            })
            : children
          : null}
      </KanbanOverlayContext.Provider>
    </DragOverlay>,
    container,
  );
}

export {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnHandle,
  KanbanItem,
  KanbanItemHandle,
  KanbanOverlay,
  useKanbanColumnContext,
  //
  type KanbanProps,
  type KanbanColumn as KanbanColumnType,
};
